import { useEffect, useState } from "react";

/**
 * Whether the page plays its scroll-driven "slides": sections pinning and
 * zooming into each other (StorySlot) and project cards stacking (Projects).
 *
 * Desktop only, and only with room to spare. On a touch screen pinning fights
 * the finger's own momentum, and on a short viewport a pinned, text-heavy
 * section mostly gets in the way of reading it — those keep plain scrolling.
 * Every scroll effect reads this one query so they can never disagree about
 * which mode the page is in.
 */
export const STORY_QUERY =
    "(min-width: 1024px) and (min-height: 720px) and (hover: hover) and (prefers-reduced-motion: no-preference)";

export function useStoryMode() {
    const [on, setOn] = useState(
        () => typeof window !== "undefined" && window.matchMedia(STORY_QUERY).matches
    );

    useEffect(() => {
        const mq = window.matchMedia(STORY_QUERY);
        const onChange = (e: MediaQueryListEvent) => setOn(e.matches);
        setOn(mq.matches);
        mq.addEventListener("change", onChange);
        return () => mq.removeEventListener("change", onChange);
    }, []);

    return on;
}

/**
 * Phones held upright: the scenes that have a portrait version (so far,
 * Experience) play it here instead of the plain layout. Kept apart from
 * STORY_QUERY so desktop story mode is untouched.
 */
export const PHONE_SCENE_QUERY = "(max-width: 767px) and (orientation: portrait) and (prefers-reduced-motion: no-preference)";

export function usePhoneScene() {
    const [on, setOn] = useState(
        () => typeof window !== "undefined" && window.matchMedia(PHONE_SCENE_QUERY).matches
    );

    useEffect(() => {
        const mq = window.matchMedia(PHONE_SCENE_QUERY);
        const onChange = (e: MediaQueryListEvent) => setOn(e.matches);
        setOn(mq.matches);
        mq.addEventListener("change", onChange);
        return () => mq.removeEventListener("change", onChange);
    }, []);

    return on;
}
